import React, { useState } from "react";
import axios from "axios";
import { Package, Search, Loader2, CheckCircle2, Clock, Truck, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = {
  payment_pending: { icon: AlertCircle, color: "#ef4444", bg: "#fee2e2", label: "Payment pending" },
  processing: { icon: Clock, color: "#b8952a", bg: "#fff4d6", label: "Processing in Dubai warehouse" },
  dispatched_soon: { icon: Package, color: "#0e0e10", bg: "#ececec", label: "Ready to ship" },
  shipped: { icon: Truck, color: "#22c55e", bg: "#dcfce7", label: "Shipped — on its way" },
};

export default function OrderTracking() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const lookup = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);

    const oid = orderId.trim();
    const em = email.trim();
    if (!oid && !em) {
      setError("Enter your Order ID or the email you used at checkout.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/orders/track`, {
        order_id: oid || undefined,
        email: em || undefined,
      });
      setResult(data);
      toast.success("Order found");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not look up your order. Try again.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const stage = result?.stage ? STAGES[result.stage] : null;
  const StageIcon = stage?.icon || Clock;

  return (
    <section
      id="track-order"
      data-testid="order-tracking-section"
      className="aurora-wrap py-20 md:py-24"
    >
      <div className="container-x max-w-3xl">
        <div className="text-center">
          <span className="section-pill">📦 Track your order</span>
          <h2 className="mt-5 text-[32px] sm:text-[42px] md:text-[52px] leading-[1.05] font-black tracking-[-0.02em] text-[#0e0e10]">
            Where&apos;s my pillow?
          </h2>
          <p className="mt-4 text-[#5a5a60] text-[15px] max-w-xl mx-auto">
            Look up your order in seconds. Use the Order ID from your confirmation email — or the email you used at checkout.
          </p>
        </div>

        <form
          onSubmit={lookup}
          className="mt-10 bg-white rounded-[28px] border border-black/5 shadow-md p-6 md:p-8"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">
                Order ID
              </label>
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="cs_test_a1B2c3…"
                data-testid="track-order-id-input"
                className="mt-1.5 w-full rounded-full border border-black/15 px-5 py-3.5 text-[14px] font-mono focus:outline-none focus:border-[#0e0e10] transition"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">
                Or email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="track-order-email-input"
                className="mt-1.5 w-full rounded-full border border-black/15 px-5 py-3.5 text-[14px] focus:outline-none focus:border-[#0e0e10] transition"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="track-order-submit"
            className="mt-5 pill-btn w-full md:w-auto md:px-12 py-3.5 text-[13px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? "Looking up…" : "Track my order"}
          </button>

          {error && (
            <div
              data-testid="track-order-error"
              className="mt-4 text-[13px] text-[#ef4444] bg-[#fee2e2] rounded-xl px-4 py-3 inline-flex items-start gap-2"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </form>

        {result && (
          <div
            data-testid="track-order-result"
            className="mt-6 bg-white rounded-[28px] border border-black/5 shadow-md p-6 md:p-8"
          >
            <div className="flex items-center gap-3">
              <span
                className="w-12 h-12 rounded-full inline-flex items-center justify-center shrink-0"
                style={{ backgroundColor: stage?.bg, color: stage?.color }}
              >
                <StageIcon size={22} />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">
                  Current status
                </div>
                <div className="text-[18px] font-black text-[#0e0e10] leading-tight">
                  {stage?.label || "Unknown"}
                </div>
              </div>
            </div>

            <p className="mt-4 text-[14px] text-[#3a3a40] leading-relaxed">{result.message}</p>

            <div className="mt-5 grid sm:grid-cols-2 gap-3 text-[13px]">
              <div className="rounded-xl bg-[#fafaf9] border border-black/5 p-3.5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">Order ID</div>
                <div className="font-mono text-[12px] text-[#0e0e10] truncate" title={result.order_id}>
                  {result.order_id}
                </div>
              </div>
              <div className="rounded-xl bg-[#fafaf9] border border-black/5 p-3.5">
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">Placed</div>
                <div className="text-[#0e0e10]">
                  {result.placed_at ? new Date(result.placed_at).toLocaleString() : "—"}
                </div>
              </div>
              {result.qty && (
                <div className="rounded-xl bg-[#fafaf9] border border-black/5 p-3.5 sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a]">Items</div>
                  <div className="text-[#0e0e10]">
                    {result.qty}× Dubai Pillow{result.qty > 1 ? "s" : ""} · {result.height} loft · {result.color}
                  </div>
                </div>
              )}
              {result.tracking_number && (
                <div className="rounded-xl bg-[#dcfce7] border border-[#22c55e]/30 p-3.5 sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#22c55e]">Tracking number</div>
                  <div className="font-mono text-[#0e0e10]">
                    {result.tracking_number} {result.carrier && <span className="text-[#5a5a60]">via {result.carrier}</span>}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 text-[12px] text-[#8a8a8a] inline-flex items-center gap-1.5">
              <Mail size={13} /> Questions? Email{" "}
              <a href="mailto:support@dubaipillows.com" className="underline">support@dubaipillows.com</a>
            </div>
          </div>
        )}

        {/* Reassurance ribbon */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] text-[#5a5a60]">
          <span className="inline-flex items-center gap-1.5"><Truck size={14} /> Free worldwide express</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> Dispatched in 24-48h</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#22c55e]" /> 100-night sleep trial</span>
          <span className="inline-flex items-center gap-1.5"><Mail size={14} /> support@dubaipillows.com</span>
        </div>
      </div>
    </section>
  );
}
